// src/utils/bandBreed.js — 787: THE ONE-BREED CROSS-FILTER, one definition, shared by both SALE surfaces.
//
// THE RULE. A band holds AT MOST ONE breed (WP-13 §2.11 / 547). Until this module the FE never narrowed
// on it: `primary_breed_id` appeared in DataEntryPage exactly once, and that occurrence was SENDING it.
// ItemOptgroups / StorageOptgroups never referenced breed at all. So both sale screens happily let the
// user build a band × bird-item pair the band cannot possibly hold, and the only feedback was a failure
// at submit — the same "offer a guaranteed failure" anti-pattern 684/777 cleaned out of these panels twice.
//
// TWO SERVER FACTS, AND THEY ARE NOT THE SAME FACT — this distinction is why the resolution below has
// two tiers rather than one:
//   · THE GATE — bandBreedGateError (api server.js:8204-8214) reads the band's PINNED primary_breed_id
//     and nothing else: pin NULL -> ALLOW (the first intake is what pins it, via autoSetPrimaryBreed),
//     pin === item.breed_id -> ALLOW, otherwise the 422 'Band "X" holds breed "A" — item "Y" is breed
//     "B". A band can hold only one breed; …'. It is wired to the INTAKE paths only: stock-entry /
//     transfer / opening on to_band_id (:7308), the purchase writer per line (:8352), openings (:8503),
//     the activation receipt backstop (:19647).
//   · THE RESOLUTION — /api/bands/list (api server.js:2364-2375) resolves a band's breed as
//     primary_breed_id, ELSE the single distinct breed among the bird items the band actually took in
//     (posted purchases + posted openings, 631), ELSE null when there is none or when two or more are
//     ambiguous. This is the RICHER answer, and it is the one a SALE wants, because a sale reads what the
//     band already HOLDS rather than what it may still receive.
//
// A SALE IS AN OUTFLOW, so the richer resolution can never over-narrow it: a band whose derived breed is
// a single "A" received nothing but A, therefore holds nothing but A, therefore can sell nothing but A.
// (On an INTAKE surface the same fallback WOULD over-narrow — it would refuse a band the gate allows —
// which is one reason this module is scoped to the sale surfaces and not dropped into the purchase form.)
//
// AND THE ONE THING THAT MUST NOT BE GOT WRONG: a band that resolves to NO breed is OPEN TO EVERY BREED,
// in both directions. Unpinned + no intake history = null here, and null is a hard "do not filter", never
// a value to match against. That is the case that keeps a first intake possible; see the null guards that
// open every function below.

import api from '../services/api';

export const isBirdItem = (item) => String(item?.category || '').toLowerCase() === 'birds';

// The band-breed rows, resolved SERVER-SIDE so the FE does not have to re-derive "one distinct breed
// among the band's bird items" out of purchase_detail + stock_entry (neither of which any sale surface
// fetches, and neither of which is worth pulling for a picker filter). /bands/list is the endpoint that
// already computes it and already emits it as `breed_id`; four dashboard surfaces call it exactly this
// way (BandsViewA:86, SeatLifecycle:316, PlanBandModal:150, BandEditModal:65). ADDED FETCH, declared:
// there is no lighter endpoint carrying the derived breed. It NEVER rejects — a failure degrades to the
// pinned-only tier below, i.e. to a wider (never a wrong) list.
export function fetchBandBreedRows() {
  return api.request('/bands/list?status=all')
    .then(r => (Array.isArray(r) ? r : []))
    .catch(() => []);
}

// bandId -> resolved breed id, or null. Mirrors resolveBreedId (api server.js:2364-2375) exactly:
// the PIN wins when set, else the server's derived single-breed answer, else null.
// `bands` is whatever the picker itself loaded (GET /api/bands returns bands.* , so primary_breed_id is
// on every row already — no extra fetch for tier 1). `listRows` is fetchBandBreedRows()'s payload.
export function buildBandBreedMap(bands, listRows) {
  const derived = {};
  for (const r of (listRows || [])) if (r && r.id) derived[r.id] = r.breed_id || null;
  const map = {};
  for (const b of (bands || [])) if (b && b.id) map[b.id] = b.primary_breed_id || derived[b.id] || null;
  for (const id of Object.keys(derived)) if (!(id in map)) map[id] = derived[id];
  return map;   // absent id => breedOf() reads undefined => falsy => OPEN. Never a guessed breed.
}

// DIRECTION 1 — BAND PICKED, NARROW THE ITEMS. Only BIRD items are breed-bound: the gate itself returns
// null for a non-bird intake (server.js:8206), so a feed or egg line is not touched by this rule and its
// items must stay on offer. A bird item with NO breed_id is also left visible: the sale writer has no
// breed gate to refuse it, so hiding it would remove a sellable item on no evidence (the gate's own
// "no breed set" refusal is an INTAKE rule, and 545/0d left 0 such items live anyway).
//
// `bandBreedId` null (unpinned, no intake history) => the list is returned UNCHANGED. Open band, open list.
// `keepItemId` is the alwaysShowId affordance storageSelectable.js:41 already uses: the line's CURRENT
// item never disappears out of its own picker, so an edit-mode pre-fill (or a legacy pair this resolution
// disagrees with) is shown as it stands rather than rendering the select blank over a still-set value —
// the exact failure 784 had to unpick. We narrow the OFFER; we never rewrite the user's line.
export function filterItemsForBandBreed(items, bandBreedId, keepItemId = null) {
  if (!bandBreedId) return items || [];
  return (items || []).filter(i =>
    !isBirdItem(i) || !i.breed_id || i.breed_id === bandBreedId || (!!keepItemId && i.id === keepItemId));
}

// DIRECTION 2 — BIRD ITEM PICKED, NARROW THE BANDS. Offer the bands whose resolved breed MATCHES, plus
// every band with NO resolved breed — an unpinned/underived band is open to any breed and must stay
// offered, or the first bird intake into it becomes unreachable from the UI.
// A non-bird item, or a bird item with no breed_id, imposes no constraint at all: the full list back.
// `keepBandId` is the same never-vanish affordance as above, for the line's currently-chosen band.
export function filterBandsForItemBreed(bands, item, breedOf, keepBandId = null) {
  if (!isBirdItem(item) || !item?.breed_id) return bands || [];
  return (bands || []).filter(b => {
    const br = breedOf(b.id);
    return !br || br === item.breed_id || (!!keepBandId && b.id === keepBandId);
  });
}
